import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * PageActionsMenu - Reusable page-level kebab menu (⋮) dropdown.
 *
 * Used in page headers for actions like Export and Select. Unlike CardMenu
 * (which uses a portal for overflow-hidden containers), this uses simple
 * absolute positioning since page headers don't clip their overflow.
 *
 * Follows the repo's dropdown convention: invisible fixed backdrop (z-40)
 * + absolutely positioned dropdown (z-50). Mobile responsive with
 * left-0 sm:left-auto sm:right-0 to prevent cutoff.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of menu item objects:
 *   { label, onClick, icon, disabled, disabledTitle, badge, badgeClass }
 *   icon: React element (e.g. <Download size={16} />) or emoji string
 *   iconClass: optional className for icon wrapper (e.g. 'text-[#52A675]')
 *   disabled: boolean — grays out item and prevents click
 *   disabledTitle: string — tooltip shown when disabled
 *   badge: optional string shown to the right (e.g. 'Pro')
 *   badgeClass: optional className for badge (default: 'text-xs text-[#F4A261] font-bold')
 *
 * @example
 * <PageActionsMenu items={[
 *   {
 *     label: 'Export',
 *     icon: isPro ? <Download size={16} /> : <Lock size={16} />,
 *     iconClass: isPro ? 'text-[#52A675]' : 'text-gray-400',
 *     onClick: () => isPro ? handleExport() : onUpgrade(),
 *     badge: !isPro ? 'Pro' : undefined,
 *   },
 *   {
 *     label: 'Select',
 *     icon: <span className="text-sm">☑️</span>,
 *     onClick: enterSelectMode,
 *   },
 * ]} />
 */
const PageActionsMenu = ({ items }) => {
    const [open, setOpen] = useState(false);

    if (!items || items.length === 0) return null;

    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="p-2.5 sm:p-3 bg-white border-2 border-gray-200 hover:border-[#52A675] hover:bg-[#52A675]/5 rounded-xl text-[#52A675] transition-colors"
                title="Actions"
                aria-label="Actions menu"
            >
                <MoreVertical size={18} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute top-full left-0 sm:left-auto sm:right-0 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden min-w-[200px]">
                        <div className="p-3">
                            {items.map((item, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        setOpen(false);
                                        if (!item.disabled && item.onClick) {
                                            item.onClick();
                                        }
                                    }}
                                    disabled={item.disabled}
                                    className={`w-full px-3 py-2.5 text-left text-sm rounded-xl flex items-center gap-3 transition-colors font-medium ${item.disabled
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-[#2F5233] hover:bg-gray-50'
                                        }`}
                                    title={item.disabled ? item.disabledTitle : undefined}
                                >
                                    {item.icon && (
                                        <span className={`flex-shrink-0 ${item.iconClass || ''}`}>
                                            {item.icon}
                                        </span>
                                    )}
                                    <span className="flex-1">{item.label}</span>
                                    {item.badge && (
                                        <span className={item.badgeClass || 'text-xs text-[#F4A261] font-bold flex-shrink-0'}>
                                            {item.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PageActionsMenu;
